import { todayStr } from "../../lib/date";
import { useEffect, useMemo, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";
import { Check, ChevronLeft, ChevronRight, X } from "lucide-react";
import {
  acceptReferral, addStudentGuardian, getDistricts, getClassGroups, getAdvisoryConfig,
} from "../../lib/api";

const STEPS = [
  { key: "basic", label: "Basic Information", required: true },
  { key: "contacts", label: "Contacts" },
  { key: "school", label: "School & Groups", required: true },
  { key: "technology", label: "Technology & Device" },
  { key: "address", label: "Address" },
  { key: "guardians", label: "Guardian Emails" },
];

// Step-by-step enrollment wizard launched from a referral's "Accept & Enroll".
// Pre-fills anything captured on the referral; required fields are flagged with
// a red asterisk; optional steps can be skipped. On finish it enrolls the
// (referral) student and provisions any guardian email logins entered.
export function EnrollmentWizard({ referral, open, onOpenChange, onEnrolled }) {
  const [stepIdx, setStepIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [districts, setDistricts] = useState([]);
  const [classGroups, setClassGroups] = useState({ morning_groups: [], afternoon_groups: [] });
  const [advisoryGroups, setAdvisoryGroups] = useState([]);
  const [form, setForm] = useState({});
  const [guardians, setGuardians] = useState([]);
  const [gEmail, setGEmail] = useState("");
  const [gName, setGName] = useState("");

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!open || !referral) return;
    setStepIdx(0);
    setGuardians([]); setGEmail(""); setGName("");
    setForm({
      first_name: referral.first_name || "",
      last_name: referral.last_name || "",
      middle_name: referral.middle_name || "",
      preferred_name: referral.preferred_name || "",
      pronouns: referral.pronouns || "",
      dob: referral.dob || "",
      gender: referral.gender || "",
      grade: referral.grade || "",
      start_date: referral.start_date || todayStr(),
      // pre-fill first contact from the referral's "best contact" fields
      first_contact_relationship: referral.first_contact_relationship || "",
      first_contact_name: referral.first_contact_name || referral.referral_best_contact || "",
      first_contact_phone: referral.first_contact_phone || referral.referral_best_phone || "",
      first_contact_email: referral.first_contact_email || "",
      second_contact_relationship: referral.second_contact_relationship || "",
      second_contact_name: referral.second_contact_name || "",
      second_contact_phone: referral.second_contact_phone || "",
      second_contact_email: referral.second_contact_email || "",
      school_district: referral.sending_district || referral.school_district || "",
      morning_group: referral.morning_group || "",
      afternoon_group: referral.afternoon_group || "",
      advisory_group: referral.advisory_group || "",
      student_email: referral.student_email || "",
      chromebook_number: referral.chromebook_number || "",
      student_password: referral.student_password || "",
      parent_account: referral.parent_account || "",
      mailing_street: referral.mailing_street || "",
      mailing_city: referral.mailing_city || "",
      mailing_state: referral.mailing_state || "",
      mailing_zip: referral.mailing_zip || "",
      mailing_notes: referral.mailing_notes || "",
    });
    Promise.all([getDistricts(), getClassGroups(), getAdvisoryConfig()])
      .then(([d, c, a]) => {
        setDistricts(d.data || []);
        setClassGroups(c.data || { morning_groups: [], afternoon_groups: [] });
        setAdvisoryGroups(a.data?.groups || []);
      })
      .catch(() => {});
  }, [open, referral]);

  const step = STEPS[stepIdx];
  const isLast = stepIdx === STEPS.length - 1;

  const basicValid = useMemo(
    () => !!(form.first_name?.trim() && form.last_name?.trim() && form.start_date),
    [form.first_name, form.last_name, form.start_date]
  );

  const districtValid = useMemo(() => !!form.school_district?.trim(), [form.school_district]);

  const next = () => {
    if (step.key === "basic" && !basicValid) {
      toast.error("First name, last name and start date are required");
      return;
    }
    if (step.key === "school" && !districtValid) {
      toast.error("Please assign a School District before continuing");
      return;
    }
    setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
  };
  const back = () => setStepIdx((i) => Math.max(i - 1, 0));

  const addGuardian = () => {
    const email = gEmail.trim();
    if (!email) return;
    if (guardians.some((g) => g.email.toLowerCase() === email.toLowerCase())) {
      toast.error("That email is already in the list");
      return;
    }
    setGuardians((p) => [...p, { email, name: gName.trim() }]);
    setGEmail(""); setGName("");
  };

  const finish = async () => {
    if (!basicValid) {
      toast.error("First name, last name and start date are required");
      setStepIdx(0);
      return;
    }
    if (!districtValid) {
      toast.error("Please assign a School District before enrolling");
      setStepIdx(STEPS.findIndex((s) => s.key === "school"));
      return;
    }
    setSaving(true);
    try {
      const res = await acceptReferral(referral.id, form);
      // Provision guardian logins after enrollment (best-effort per email)
      let gOk = 0;
      for (const g of guardians) {
        try {
          await addStudentGuardian(referral.id, { email: g.email, name: g.name });
          gOk += 1;
        } catch {
          toast.error(`Could not add guardian ${g.email}`);
        }
      }
      toast.success(
        `Enrolled ${form.first_name} ${form.last_name}${gOk ? ` · ${gOk} guardian login(s) created` : ""}`
      );
      onOpenChange(false);
      onEnrolled?.(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to enroll student");
    } finally {
      setSaving(false);
    }
  };

  const Req = () => <span className="text-red-600">&nbsp;*</span>;
  const field = (key, label, opts = {}) => (
    <div className="space-y-1">
      <Label className="text-xs">{label}{opts.required ? <Req /> : null}</Label>
      <Input
        type={opts.type || "text"}
        value={form[key] || ""}
        onChange={(e) => set(key, opts.upper ? e.target.value.toUpperCase() : e.target.value)}
        placeholder={opts.placeholder || ""}
        maxLength={opts.maxLength}
        data-testid={`wizard-${key}`}
      />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-green-700">
            Enroll {referral?.first_name} {referral?.last_name}
          </DialogTitle>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex flex-wrap items-center gap-1.5 pt-1" data-testid="wizard-steps">
          {STEPS.map((s, i) => (
            <span
              key={s.key}
              className={`text-xs px-2 py-1 rounded-full font-medium flex items-center gap-1 ${
                i === stepIdx
                  ? "bg-green-600 text-white"
                  : i < stepIdx
                  ? "bg-green-100 text-green-700"
                  : "bg-slate-100 text-slate-500"
              }`}
            >
              {i < stepIdx ? <Check className="h-3 w-3" /> : `${i + 1}.`} {s.label}
              {s.required ? <Req /> : null}
            </span>
          ))}
        </div>

        <div className="pt-3" data-testid={`wizard-step-${step.key}`}>
          {step.key === "basic" && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {field("first_name", "First Name", { required: true })}
              {field("middle_name", "Middle Name")}
              {field("last_name", "Last Name", { required: true })}
              {field("preferred_name", "Preferred Name")}
              <div className="space-y-1">
                <Label className="text-xs">Pronouns</Label>
                <Select value={form.pronouns || ""} onValueChange={(v) => set("pronouns", v)}>
                  <SelectTrigger data-testid="wizard-pronouns"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="he/him">He/Him</SelectItem>
                    <SelectItem value="she/her">She/Her</SelectItem>
                    <SelectItem value="they/them">They/Them</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {field("dob", "Date of Birth", { type: "date" })}
              {field("gender", "Gender", { placeholder: "e.g., Male, Female, Non-binary" })}
              {field("grade", "Grade Level", { placeholder: "e.g., 9th" })}
              {field("start_date", "Start Date", { required: true, type: "date" })}
            </div>
          )}

          {step.key === "contacts" && (
            <div className="space-y-4">
              <div>
                <p className="text-sm font-semibold text-slate-700 mb-2">First Contact</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {field("first_contact_relationship", "Relationship")}
                  {field("first_contact_name", "Name")}
                  {field("first_contact_phone", "Phone")}
                  {field("first_contact_email", "Email", { type: "email" })}
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-700 mb-2">Second Contact</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {field("second_contact_relationship", "Relationship")}
                  {field("second_contact_name", "Name")}
                  {field("second_contact_phone", "Phone")}
                  {field("second_contact_email", "Email", { type: "email" })}
                </div>
              </div>
            </div>
          )}

          {step.key === "school" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">School District<Req /></Label>
                <Select value={form.school_district || ""} onValueChange={(v) => set("school_district", v)}>
                  <SelectTrigger data-testid="wizard-school_district"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {districts.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {!form.school_district?.trim() && (
                  <p className="text-xs text-red-600" data-testid="wizard-district-required-hint">
                    Required — classifies this student under the correct district on the Referral Trends Dashboard.
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Morning Group</Label>
                <Select value={form.morning_group || ""} onValueChange={(v) => set("morning_group", v)}>
                  <SelectTrigger data-testid="wizard-morning_group"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {(classGroups.morning_groups || []).map((g) => <SelectItem key={g.key} value={g.key}>{g.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Afternoon Group</Label>
                <Select value={form.afternoon_group || ""} onValueChange={(v) => set("afternoon_group", v)}>
                  <SelectTrigger data-testid="wizard-afternoon_group"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {(classGroups.afternoon_groups || []).map((g) => <SelectItem key={g.key} value={g.key}>{g.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Advisory</Label>
                <Select value={form.advisory_group || "none"} onValueChange={(v) => set("advisory_group", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="wizard-advisory_group"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not Assigned</SelectItem>
                    {advisoryGroups.map((g) => <SelectItem key={g.group} value={g.group}>{g.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {step.key === "technology" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {field("student_email", "Student Email / Login", { placeholder: "student@school.edu" })}
              {field("chromebook_number", "Chromebook #", { placeholder: "CB-101" })}
              {field("student_password", "Current Password")}
              {field("parent_account", "Parent Account / Staff Reset")}
            </div>
          )}

          {step.key === "address" && (
            <div className="space-y-3">
              {field("mailing_street", "Street Address", { placeholder: "123 Main St" })}
              <div className="grid grid-cols-6 gap-2">
                <div className="col-span-3">{field("mailing_city", "City")}</div>
                <div className="col-span-1">{field("mailing_state", "ST", { upper: true, maxLength: 2 })}</div>
                <div className="col-span-2">{field("mailing_zip", "ZIP")}</div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Address Notes</Label>
                <Textarea rows={2} value={form.mailing_notes || ""} onChange={(e) => set("mailing_notes", e.target.value)} placeholder="Apt #, directions, etc." data-testid="wizard-mailing_notes" />
              </div>
            </div>
          )}

          {step.key === "guardians" && (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Optionally give parents/guardians portal access. A temporary password is emailed to each on enrollment. You can also add these later from the student file.
              </p>
              <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
                <div className="flex-1 space-y-1">
                  <Label className="text-xs">Guardian Email</Label>
                  <Input type="email" value={gEmail} onChange={(e) => setGEmail(e.target.value)} placeholder="parent@example.com" data-testid="wizard-guardian-email" />
                </div>
                <div className="flex-1 space-y-1">
                  <Label className="text-xs">Guardian Name (optional)</Label>
                  <Input value={gName} onChange={(e) => setGName(e.target.value)} placeholder="e.g. Jane Doe" data-testid="wizard-guardian-name" />
                </div>
                <Button type="button" variant="outline" onClick={addGuardian} data-testid="wizard-guardian-add">Add</Button>
              </div>
              {guardians.length > 0 && (
                <div className="border rounded-md divide-y" data-testid="wizard-guardian-list">
                  {guardians.map((g) => (
                    <div key={g.email} className="flex items-center justify-between gap-2 p-2 text-sm">
                      <span>{g.name ? `${g.name} · ` : ""}{g.email}</span>
                      <button type="button" className="text-red-600" onClick={() => setGuardians((p) => p.filter((x) => x.email !== g.email))} data-testid={`wizard-guardian-remove-${g.email}`}>
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex-row justify-between gap-2 sm:justify-between">
          <Button variant="outline" onClick={back} disabled={stepIdx === 0} data-testid="wizard-back-btn">
            <ChevronLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          {isLast ? (
            <Button className="bg-green-700 hover:bg-green-800" onClick={finish} disabled={saving} data-testid="wizard-finish-btn">
              {saving ? "Enrolling..." : "Enroll Student"}
            </Button>
          ) : (
            <Button onClick={next} data-testid="wizard-next-btn">
              {step.required ? "Next" : "Next / Skip"} <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default EnrollmentWizard;
